import { useState } from 'react';
import { Link } from 'react-router-dom';
import { EngineeringNav } from '@/components/EngineeringNav';
import { ArrowRight, X } from 'lucide-react';

export default function EngineeringPortfolio() {
  const [selectedProject, setSelectedProject] = useState(null);
  
  const projects = [
    {
      id: 1,
      title: 'Engine Mount Brackets',
      category: '3D Scanning, CAD, CNC Plasma Cutting and Fabrication',
      description: 'Engine mount brackets for fitting a BMW engine into a Landrover',
      image: 'https://customer-assets.emergentagent.com/job_cnc-artisan/artifacts/l50pw01w_image.png',
      details: 'Custom engineered brackets designed through 3D scanning of existing components, CAD modeling for perfect fit, CNC plasma cutting, and professional fabrication.'
    },
    {
      id: 2,
      title: 'Agricultural Machinery Plates',
      category: 'CNC Plasma Cutting',
      description: 'Heavy plates cut from 20mm thick mild steel, for an implement adaptor on a Skid Steer',
      image: 'https://customer-assets.emergentagent.com/job_cnc-artisan/artifacts/o0hwq7lx_Gemini_Generated_Image_l9c8d6l9c8d6l9c8.png',
      details: 'Precision CNC plasma cutting of heavy-duty 20mm mild steel plates for agricultural equipment. Designed for durability and perfect fit.'
    },
    {
      id: 3,
      title: 'Industrial Bracketry',
      category: 'CAD Design',
      description: 'Bracket to support valve for an industrial process',
      image: 'https://customer-assets.emergentagent.com/job_cnc-artisan/artifacts/p4196x4a_image.png',
      details: 'Custom CAD design of industrial support bracketry, engineered for structural integrity and precise positioning of process valves.'
    },
    {
      id: 4,
      title: 'Heritage Part Reproduction',
      category: '3D Scanning',
      description: 'Scan a broken casting from a steam engine, to digitise the geometry for reproduction',
      image: 'https://customer-assets.emergentagent.com/job_cnc-artisan/artifacts/0qgk0eco_image.png',
      details: 'High-resolution 3D scanning of irreplaceable heritage steam engine components, capturing precise geometry for faithful reproduction.'
    },
    {
      id: 5,
      title: 'Automotive Components',
      category: 'Technical Drawing',
      description: 'Produced 2D technical drawings, for manufacturing a cylinder block, from existing CAD data.',
      image: 'https://customer-assets.emergentagent.com/job_cnc-artisan/artifacts/swlq1n1y_Screenshot%202026-02-04%20151519.jpg',
      details: 'Comprehensive technical drawings produced from existing CAD data, ready for manufacturing with full dimensions and tolerances.'
    },
    {
      id: 6,
      title: 'Residential, Ornamental Gates',
      category: 'CNC Plasma Cutting, MIG Welding',
      description: 'Component pieces cut and welded together, left to go rusty after installation.',
      image: 'https://customer-assets.emergentagent.com/job_cnc-artisan/artifacts/5n4q1bp4_image.png',
      details: 'Custom ornamental gates with CNC plasma cut components, MIG welded assembly, designed to develop a natural rust patina over time.'
    },
  ];

  const categories = ['All', ...new Set(projects.map(p => p.category))];
  const [activeCategory, setActiveCategory] = useState('All');
  
  const filteredProjects = activeCategory === 'All' 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-black" data-testid="engineering-portfolio">
      <EngineeringNav />
      
      {/* Hero */}
      <section className="bg-[#0a0a0a] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-nunito text-sm text-[#F54021] mb-4 tracking-wider uppercase font-bold">Portfolio</p>
            <h1 className="font-nunito text-4xl lg:text-5xl font-extrabold text-white mb-6">
              Our Work Speaks for Itself
            </h1>
            <p className="font-nunito text-lg text-gray-400">
              Browse through our completed projects showcasing precision engineering and quality craftsmanship.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="border-b border-[#333333] sticky top-16 bg-black z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-full font-nunito text-sm whitespace-nowrap transition-colors font-semibold ${
                  activeCategory === cat
                    ? 'bg-[#F54021] text-white'
                    : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#2a2a2a] border border-[#333333]'
                }`}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <div 
                key={project.id}
                className="card-engineering bg-[#1a1a1a] rounded-2xl border border-[#333333] overflow-hidden cursor-pointer group opacity-0 animate-slide-up"
                style={{ animationFillMode: 'forwards', animationDelay: `${index * 100}ms` }}
                onClick={() => setSelectedProject(project)}
                data-testid={`project-card-${project.id}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="p-6">
                  <span className="font-nunito text-xs text-[#F54021] tracking-wider uppercase font-bold">{project.category}</span>
                  <h3 className="font-nunito font-bold text-lg text-white mt-2 mb-2">{project.title}</h3>
                  <p className="font-nunito text-sm text-gray-400 line-clamp-2">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedProject(null)}
        >
          <div 
            className="bg-[#1a1a1a] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-auto border border-[#333333]"
            onClick={(e) => e.stopPropagation()}
            data-testid="project-modal"
          >
            <div className="relative">
              <img 
                src={selectedProject.image} 
                alt={selectedProject.title}
                className="w-full h-64 object-cover"
              />
              <button 
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black rounded-full flex items-center justify-center hover:bg-[#1a1a1a] border border-[#333333]"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <div className="p-8">
              <span className="font-nunito text-xs text-[#F54021] tracking-wider uppercase font-bold">{selectedProject.category}</span>
              <h2 className="font-nunito font-bold text-2xl text-white mt-2 mb-4">{selectedProject.title}</h2>
              <p className="font-nunito text-gray-400 mb-4">{selectedProject.description}</p>
              <div className="bg-black rounded-xl p-4 border border-[#333333]">
                <p className="font-nunito text-sm text-gray-300">{selectedProject.details}</p>
              </div>
              <Link 
                to="/engineering/quote"
                className="mt-6 inline-flex items-center gap-2 text-[#F54021] font-nunito font-semibold hover:gap-3 transition-all"
              >
                Request a similar project
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* CTA */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-nunito text-3xl font-bold text-white mb-4">
            Have a Project in Mind?
          </h2>
          <p className="font-nunito text-gray-400 mb-8">
            Let's discuss how we can bring your vision to reality.
          </p>
          <Link 
            to="/engineering/quote"
            className="btn-engineering inline-flex items-center gap-2 px-8 py-4"
            data-testid="request-quote-btn"
          >
            Request a Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-[#333333] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-nunito text-sm text-gray-500">© 2024 BJ Engineering. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
